// D1: Tauri 自动更新 — 客户端封装层
//
// 工作流:
//   1. 启动后冷启动 60s + 每 4h 调 check()
//   2. 有新版 -> 触发 onUpdateAvailable(update) -> 主程序弹 UpdaterDialog
//   3. 用户点"立即更新" -> downloadAndInstall(progress) -> relaunch()
//   4. 用户点"稍后再说" -> 记 'snoozed:<version>' 在 localStorage,
//      该版本不再主动弹 (除非 min_version 强制), 直到下一个新版本
//   5. min_version 强制场景: dialog 只显示"立即更新", 拦截整 app 输入

import { check, type Update } from "@tauri-apps/plugin-updater";
import { relaunch } from "@tauri-apps/plugin-process";

import { isTauriDesktop } from "./tauri-agent-host";

// D1-UX §3: 每个版本最多弹 2 次, 用计数器记录
// localStorage 存 JSON: { version: "1.2.0", count: 1 }
const SNOOZE_KEY = "chatbox.updater.snoozeStateV1";
const MAX_SNOOZE_BEFORE_PERMANENT = 2;

export interface UpdateInfo {
  version: string;
  currentVersion: string;
  date?: string;
  body?: string;
  /** D1 扩展字段: 从 manifest.json body 解析的 min_version (>= 当前版本必须强制更新) */
  minVersion?: string;
  /** 是否强制 (min_version 不允许稍后) */
  forced: boolean;
  /** 内部 update 句柄, 给 install / download 用 */
  raw: Update;
}

export type UpdateProgress =
  | { phase: "started"; total?: number }
  | { phase: "progress"; downloaded: number; total?: number }
  | { phase: "finished" };

/** 后台尝试一次检查; 静默返回 null 表示无更新或检查失败 */
export async function checkForUpdate(): Promise<UpdateInfo | null> {
  if (!isTauriDesktop()) return null;
  let update: Update | null = null;
  try {
    update = await check();
  } catch (err) {
    console.warn("[updater] check failed:", err);
    return null;
  }
  if (!update) return null;

  // D1: 解析 release notes 里嵌的 min_version (协议自定义, 见 D1-auto-updater-design §7)
  const body = update.body ?? "";
  const minVer = parseMinVersion(body);
  const forced = minVer !== null && compareSemver(update.currentVersion, minVer) < 0;

  return {
    version: update.version,
    currentVersion: update.currentVersion,
    date: update.date,
    body,
    minVersion: minVer ?? undefined,
    forced,
    raw: update,
  };
}

interface SnoozeState {
  version: string;
  count: number;
}

function readSnoozeState(): SnoozeState | null {
  try {
    const raw = localStorage.getItem(SNOOZE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as SnoozeState;
    if (typeof v.version === "string" && typeof v.count === "number") return v;
    return null;
  } catch {
    return null;
  }
}

/** 用户主动"稍后" -> 计数 +1; 达到上限即永久跳过该版本. */
export function snoozeVersion(version: string): void {
  try {
    const cur = readSnoozeState();
    const next: SnoozeState =
      cur && cur.version === version
        ? { version, count: cur.count + 1 }
        : { version, count: 1 };
    localStorage.setItem(SNOOZE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

/** 该版本是否已永久跳过 (snooze >= 2). 启动时调一次跳过则本次不再弹. */
export function isVersionPermanentlySnoozed(version: string): boolean {
  const s = readSnoozeState();
  return (
    s !== null &&
    s.version === version &&
    s.count >= MAX_SNOOZE_BEFORE_PERMANENT
  );
}

/** D1-UX §3 兼容老代码 export (内部判断用 permanently 版本) */
export const isVersionSnoozed = isVersionPermanentlySnoozed;

/** 下载并安装更新, 然后 relaunch */
export async function downloadAndInstall(
  info: UpdateInfo,
  onProgress?: (p: UpdateProgress) => void,
): Promise<void> {
  let downloaded = 0;
  let total: number | undefined = undefined;
  await info.raw.downloadAndInstall((event) => {
    if (event.event === "Started") {
      total = event.data.contentLength;
      onProgress?.({ phase: "started", total });
    } else if (event.event === "Progress") {
      downloaded += event.data.chunkLength;
      onProgress?.({ phase: "progress", downloaded, total });
    } else if (event.event === "Finished") {
      onProgress?.({ phase: "finished" });
    }
  });
  await relaunch();
}

// ---------- helpers ----------

/** 从 release notes 找 "min_version: 1.2.0" 行. 没有就是非强制. */
function parseMinVersion(body: string): string | null {
  const m = body.match(/min_version\s*[:=]\s*([0-9]+(?:\.[0-9]+){1,2})/i);
  return m ? m[1] : null;
}

/** 1 if a > b, 0 if eq, -1 if a < b. 简易 semver, 容错非数字 */
function compareSemver(a: string, b: string): number {
  const pa = a.split(".").map((x) => parseInt(x, 10) || 0);
  const pb = b.split(".").map((x) => parseInt(x, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const va = pa[i] ?? 0;
    const vb = pb[i] ?? 0;
    if (va > vb) return 1;
    if (va < vb) return -1;
  }
  return 0;
}
